import { TAttributeParameter } from 'components/FlowBuilder/FlowSidePanels/types';
import { convertPeriodToSeconds } from 'helpers/templated-experience.helper';
import { TContextAttribute } from 'hooks/useContextPlugins';

type CtxParam = {
  name: string;
  valueOptions?: {
    key: string;
    name: string;
  }[];
};

export type TimeContextAttribute = {
  key: string;
  name: string;
  ctxParams: CtxParam[];
};

/**
 * Filters for plugin attributes with type dateTime.
 * */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getTimeCtxPluginAttributes = (plugins: any) => {
  const _timeCtxAttrs: TimeContextAttribute[] = [];

  if (plugins?.length) {
    for (const plugin of plugins) {
      for (const attr in plugin.values) {
        if (plugin.values[attr]?.isTimeContext) {
          const key = plugin.values[attr].uid;
          const ctxParams: CtxParam[] = [];
          if (plugin.values[attr].parameters?.length) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            plugin.values[attr].parameters.forEach((param: any) =>
              ctxParams.push({
                name: param.key,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                valueOptions: param.valueTypeOptions?.map((opt: any) => {
                  return { key: opt.value, name: opt.displayName || opt.value };
                }),
              }),
            );
          }

          _timeCtxAttrs.push({ key, name: `${plugin.name} / ${plugin.values[attr].name}`, ctxParams });
        }
      }
    }
  }

  return _timeCtxAttrs;
};

/**
 * Prepare payload for updating after/before preconditions.
 * */
export const genBeforeAfterPayload = (
  stepIdx: number,
  type: 'start' | 'end',
  value: number,
  period: string,
  attribute: string,
  params: string[],
  preconditionKey?: string,
) => {
  const splitModifier = attribute.split('.');
  const sanitizedParams = params.map((param) => param || '*');
  const operator =
    preconditionKey === 'before-unique-event' ? ' - ' : preconditionKey === 'after-unique-event' ? ' + ' : '';
  const timeValue = convertPeriodToSeconds(value, period);
  const modifier = splitModifier.concat(sanitizedParams).join('.') + operator + timeValue;

  return {
    stepIdx,
    type,
    modifier,
    target: 'context',
    // logic and predicates are necessary, so they're just set to be true for
    // any time value that comes in for the specified plugin/attribute
    logic: '1',
    predicates: {
      '1': {
        name: 'greq',
        parameters: [
          {
            type: 'context',
            plugin: splitModifier.slice(0, 3).join('.'),
            attribute: splitModifier.slice(3).concat(sanitizedParams).join('.'),
          },
          {
            type: 'constant',
            value: '0',
            dataType: 'int',
          },
        ],
      },
    },
  };
};

/**
 * Takes in a `ctxAttrString` (without handlebars) and splits it into attribute uid, params, key, and default value.
 * Will throw if the `ctxAttrString` is invalid, or not found in `allAttributes`.
 * @param ctxAttrString The context attribute string to parse (without handlebars).
 * @param allAttributes An array of all available context attributes to match against.
 * @returns An object containing:
 *   - displayName: A reformatted `ctxAttrString` for display purposes.
 *   - attributeId: The unique identifier of the attribute (i.e. `attribute.uid`).
 *   - attributeMapKey: The map key of map[string]x type attributes, if applicable.
 *   - attributeParameters: The parameters associated with the attribute, if any.
 *   - defaultValue: The default value for the attribute, if any.
 *   - errorMessage: An error message if the `ctxAttrString` is invalid.
 */
export const splitCtxString = (ctxAttrString: string, allAttributes: TContextAttribute[]) => {
  const commaDelimitedCtxParts = ctxAttrString.split(',');
  if (commaDelimitedCtxParts.length > 2) {
    throw new Error('Action needed: Invalid context attribute');
  }

  const periodDelimitedCtxParts = commaDelimitedCtxParts[0].split('.');
  if (periodDelimitedCtxParts.length < 4) {
    throw new Error('Action needed: Invalid context attribute');
  }

  const isParameterized = periodDelimitedCtxParts[3] === 'query';
  const attributeId = isParameterized
    ? periodDelimitedCtxParts.slice(0, 5).join('.')
    : periodDelimitedCtxParts.slice(0, 4).join('.');

  const attribute = allAttributes.find((attr) => attr.uid === attributeId);
  if (!attribute) {
    throw new Error('Action needed: Invalid context attribute');
  }

  let errorMessage = '';

  const isMapType = attribute.valueType.includes('map[');
  const attributeMapKey = isMapType ? (isParameterized ? periodDelimitedCtxParts[5] : periodDelimitedCtxParts[4]) : '';
  if (isMapType && (!attributeMapKey || !attribute.valueKeys?.includes(attributeMapKey))) {
    errorMessage = 'Action needed: edit this attribute for missing value(s)';
  }
  const parameters = isParameterized ? periodDelimitedCtxParts.slice(isMapType ? 6 : 5) : [];
  if ((attribute.parameters?.length ?? 0) !== parameters.length) {
    errorMessage = 'Action needed: edit this attribute for missing value(s)';
  }
  const attributeParameters: TAttributeParameter[] =
    attribute.parameters?.map((p, i) => ({
      name: p.name || p.key,
      value: parameters[i] ?? '',
      options: p.valueTypeOptions?.map((opt) => ({ key: opt.value, name: opt.displayName })) ?? [],
      type: p.valueType,
    })) ?? [];

  const defaultValue = commaDelimitedCtxParts[1] ?? '';

  const displayNameParts: string[] = [];
  if (isParameterized) {
    displayNameParts.push(`${periodDelimitedCtxParts[2]}.${periodDelimitedCtxParts[4]}`);
  } else {
    displayNameParts.push(periodDelimitedCtxParts.slice(2, 4).join('.'));
  }
  if (isMapType) {
    displayNameParts.push(`.${attributeMapKey}`);
  }
  if (isParameterized && parameters.length > 0) {
    displayNameParts.push(`: ${parameters.join(', ')}`);
  }
  if (defaultValue) {
    displayNameParts.push(`| Default: ${defaultValue}`);
  }
  const displayName = displayNameParts.join('');

  return {
    displayName,
    attributeId,
    attributeMapKey,
    attributeParameters,
    defaultValue,
    errorMessage,
  };
};
